import React from 'react';
import './Team.css';
import TeamHeroSection from '../components/hero/TeamHeroSection';
import MetricsStrip from '../components/premium/MetricsStrip';
import ScrollReveal from '../components/premium/ScrollReveal';
import FounderCard, { type FounderData } from '../components/premium/FounderCard';
import ValuesGrid from '../components/premium/ValuesGrid';
import FloatingOrbs from '../components/premium/FloatingOrbs';
import PremiumCta from '../components/premium/PremiumCta';
import { teamMetrics } from '../data/solutionContent';

const founders: FounderData[] = [
  {
    id: 'carolina-arango',
    name: 'Carolina Arango',
    role: 'CEO & Co-Founder',
    description:
      'Strategic leader with extensive experience in commercial development, investor relations, and startup scaling. Expert in building strategic partnerships, driving business growth, and securing funding to accelerate company expansion in competitive markets.',
    image: '/images/caro_arango.png',
    hasExit: false,
  },
  {
    id: 'luisa-abello',
    name: 'Luisa Abello',
    role: 'CPO & Co-Founder',
    description:
      'Passionate entrepreneur dedicated to building solutions that leave the world better than we found it. I deeply believe in the transformative power of technology when put at the service of people. My late diagnosis of Dysautonomia has connected me with the real challenges of healthcare access, facing complex symptoms daily that have not been an obstacle, but rather a motivation to create impact. Today I lead product at Lummo with the conviction that clinical innovation can (and should) be faster, more human and accessible to everyone.',
    image: '/images/luisa_abello.png',
    hasExit: true,
  },
  {
    id: 'javier-contreras',
    name: 'Javier Contreras',
    role: 'CTO & Co-Founder',
    description:
      'Engineering leader with 10+ years shipping machine learning and AI in production — long before it was the default playbook. Former Rappi; former CTO at Valu, an AI-powered recruitment company he helped scale through a successful exit. He brings that same disciplined, product-grade execution to Lummo: resilient infrastructure, clear ownership of data and compliance signals, and systems patients and clinical teams can actually trust.',
    image: '/images/javier_contreras.png',
    hasExit: true,
  },
  {
    id: 'valentina-aldana',
    name: 'Valentina Aldana',
    role: 'COO & Co-Founder',
    description:
      "Psychologist passionate about mental health, research and clinical innovation. I firmly believe in the power of science to transform lives and in technology as a catalyst for change in healthcare access. I enjoy exploring public health topics, documentary research and the impact of healthcare systems on people's quality of life. Always seeking to generate real impact through knowledge and service.",
    image: '/images/valentina_aldana.png',
    hasExit: true,
  },
  {
    id: 'alfredo-hernandez',
    name: 'Alfredo Hernandez',
    role: 'Chief Clinical Officer',
    description:
      'Internal Medicine Specialist with an MSc in Clinical Epidemiology and an MSc in Innovation Management. Over 20 years of experience in clinical practice and scientific research. I have served as research center director, in teaching, and in senior medical leadership.',
    image: '/images/doctor_alfredo.png',
    hasExit: false,
  },
  {
    id: 'veronica-gomez',
    name: 'Veronica Gomez',
    role: 'VP Science & Research & Founding Team',
    description:
      'Clinical research expert with over a decade of experience in clinical trials, specializing in site operations and trial execution. Extensive background in scientific research methodology and regulatory compliance, ensuring rigorous standards in clinical innovation.',
    image: '/images/vero_gomez.png',
    hasExit: false,
  },
];

const Team: React.FC = () => (
  <div className="team-page">
    <section className="hero-wrapper">
      <TeamHeroSection />
    </section>

    <MetricsStrip metrics={teamMetrics} />

    <section className="company-story team-section-premium">
      <div className="container">
        <ScrollReveal className="story-content">
          <h2>Why Lummo Exists</h2>
          <div className="story-text story-text--columns">
            <p>
              Lummo was born from something deeply personal: an absence. In our own families, we faced complex diseases like systemic sclerosis and thyroid cancer. We confronted uncertainty, misinformation, and the anguish of not knowing if there was a clinical trial that could make a difference.
            </p>
            <p>
              That experience awakened a conviction in us: access to medical innovation cannot depend on chance, bureaucracy, or where you were born.
            </p>
            <p>
              Lummo emerges as a response to a reality that repeats throughout Latin America: slow, fragmented, expensive, and deeply unequal clinical processes. Starting a clinical trial can take months, between forms, regulations, approvals, and recruitment. Meanwhile, patients wait. Or run out of options.
            </p>
            <p>
              That is why we created Lummo: a technological infrastructure designed to transform clinical trials from the core. We automate regulatory processes, connect sponsors with CROs and hospitals, accelerate recruitment, and, above all, give visibility to those who need it most: patients.
            </p>
            <p>
              We believe that every day of progress counts. And that with technology, empathy, and rigor, we can make the future of health arrive faster, and to more people.
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <section className="founders-showcase team-section-premium">
      <div className="container">
        <ScrollReveal>
          <h2>Meet Our Team</h2>
        </ScrollReveal>
        <div className="founders-grid-premium">
          {founders.map((founder, index) => (
            <FounderCard key={founder.id} founder={founder} index={index} />
          ))}
        </div>
      </div>
    </section>

    <section className="company-mission team-section-premium team-section-premium--mission">
      <FloatingOrbs />
      <div className="container">
        <ScrollReveal className="mission-content">
          <h2>Our Foundation</h2>
          <div className="mission-grid">
            <article className="mission-text mission-text--premium">
              <h3>Vision</h3>
              <p>
                To be the leading infrastructure for clinical trials in Latin America, reducing activation times and costs by half through intelligent, accessible, and regulatorily solid technology.
              </p>
            </article>
            <article className="mission-text mission-text--premium">
              <h3>Mission</h3>
              <p>
                We bring clinical innovation to those who need it most, employing technology to transform health from clinical trials.
              </p>
            </article>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <ValuesGrid />

    <PremiumCta
      title="Build the future of trials with us"
      paragraphs={['We are hiring builders who care about patients, science, and LATAM.']}
      tagline="Book a demo — or reach out directly."
      primaryHref="/contact#lummo-demo-form"
      primaryLabel="Book a demo"
    />
  </div>
);

export default Team;
